export const ROUTE_PATHS = {
  HOME: '/',
  PLATFORM: '/platform',
  ASSISTANTS: '/assistants',
  FOR_BUSINESS: '/for-business',
  SCHOOLS: '/schools',
  FAMILY_ACCESS: '/family-access',
  PARTNERS: '/partners',
  ABOUT: '/about',
  CONTACT: '/contact',
} as const;

export interface Assistant {
  id: string;
  name: string;
  category: 'Learn & Grow' | 'Money & Work' | 'Home & Support';
  icon: string;
  description: string;
  examplePrompt: string;
}

export interface Industry {
  id: string;
  name: string;
  icon: string;
  description: string;
}

export interface PricingPlan {
  id: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  members: number;
  assistants: number | string;
  messages: number | string;
  features: string[];
  highlighted?: boolean;
}

export interface NavItem {
  label: string;
  href: string;
}

export interface Partner {
  id: string;
  type: string;
  icon: string;
  title: string;
  description: string;
}

export const ASSISTANTS_LIST: Assistant[] = [
  {
    id: 'school-tutor',
    name: 'School Tutor',
    category: 'Learn & Grow',
    icon: 'BookOpen',
    description: 'Curriculum-aligned academic support for all grades — covering homework help, exam prep, study plans and practice tests via WhatsApp.',
    examplePrompt: 'Help my Grade 9 child prepare for their Maths exam',
  },
  {
    id: 'teacher-assistant',
    name: 'Teacher Assistant',
    category: 'Learn & Grow',
    icon: 'GraduationCap',
    description: 'Saves teachers time with ready-to-use lesson plans, worksheets, assessments, rubrics, report comments and term planning — all aligned to country curricula.',
    examplePrompt: 'Create a Grade 8 Life Sciences lesson plan on ecosystems',
  },
  {
    id: 'legal-guide',
    name: 'Legal Guide',
    category: 'Home & Support',
    icon: 'Scale',
    description: 'Practical legal guidance on labour law, tenant disputes, consumer rights, debt matters, contracts, traffic fines and more — with draft document support.',
    examplePrompt: 'My landlord is refusing to return my deposit',
  },
  {
    id: 'family-health-guide',
    name: 'Family Health Guide',
    category: 'Home & Support',
    icon: 'HeartPulse',
    description: 'Preventative health education and symptom navigation for the whole family — covering acute illness, chronic conditions, child health, vaccinations and first aid.',
    examplePrompt: 'My child has had a fever for two days, what should I do?',
  },
  {
    id: 'farm-assist',
    name: 'Farm Assist',
    category: 'Home & Support',
    icon: 'Sprout',
    description: 'Practical support for subsistence, emerging and commercial farmers — covering crops, livestock, soil health, pest control, seasonal planning and farm budgeting.',
    examplePrompt: 'My tomato leaves are yellowing, what could be wrong?',
  },
  {
    id: 'my-debt-help',
    name: 'My Debt Help',
    category: 'Money & Work',
    icon: 'CreditCard',
    description: 'Structured debt education and recovery guidance — covering debt prioritisation, repayment planning, affordability calculations and creditor negotiation preparation.',
    examplePrompt: 'I have three debts and do not know which to pay first',
  },
  {
    id: 'my-daily-faith',
    name: 'My Daily Faith',
    category: 'Home & Support',
    icon: 'Sun',
    description: 'Daily spiritual encouragement across major faith traditions — offering devotionals, sacred text excerpts, prayer guidance and reflective prompts in your language.',
    examplePrompt: 'Share a morning devotional to start my day',
  },
  {
    id: 'my-pet-care',
    name: 'My Pet Care',
    category: 'Home & Support',
    icon: 'PawPrint',
    description: 'General health guidance, vaccination schedules, nutrition, grooming and parasite prevention for all pet types — with emergency red-flag alerts when needed.',
    examplePrompt: 'How often should I deworm my dog and what do I use?',
  },
  {
    id: 'my-life-coach',
    name: 'My Life Coach',
    category: 'Money & Work',
    icon: 'Compass',
    description: 'A practical life guide for young adults — covering personal productivity, goal setting, tax and admin, financial navigation and career planning with step-by-step support.',
    examplePrompt: 'Help me set a realistic goal and build a plan around it',
  },
  {
    id: 'learn-a-trade',
    name: 'Learn a Trade',
    category: 'Money & Work',
    icon: 'Hammer',
    description: 'Entry-level vocational skills for unemployed individuals and side hustlers — covering practical trades like plumbing, carpentry, tiling, sewing and appliance repair with pricing and business basics.',
    examplePrompt: 'I want to learn basic tiling — where do I start?',
  },
  {
    id: 'know-phones-tech',
    name: 'Know Phones & Tech',
    category: 'Learn & Grow',
    icon: 'Smartphone',
    description: 'Digital literacy and cybersecurity guidance for everyday users — covering smartphone setup, app usage, fraud prevention, phishing detection and online scam awareness.',
    examplePrompt: 'How do I know if a WhatsApp message is a scam?',
  },
  {
    id: 'family-marriage',
    name: 'Family & Marriage',
    category: 'Home & Support',
    icon: 'Heart',
    description: 'Relationship-strengthening guidance for couples and families — covering communication, conflict resolution, financial harmony, parenting alignment and emotional resilience.',
    examplePrompt: 'My partner and I keep arguing about money, how do we handle this better?',
  },
  {
    id: 'home-fix-build',
    name: 'Home Fix & Build',
    category: 'Home & Support',
    icon: 'Wrench',
    description: 'Household repair, DIY renovation and appliance troubleshooting support — covering plumbing, electrical basics, material calculations, vehicle maintenance and when to call a professional.',
    examplePrompt: 'My geyser is leaking, what should I check first?',
  },
  {
    id: 'mental-support',
    name: 'Mental Support',
    category: 'Home & Support',
    icon: 'Brain',
    description: 'Non-clinical emotional support and resilience guidance — covering stress and anxiety coping tools, breathing exercises, grounding techniques and crisis referral when needed.',
    examplePrompt: 'I have been feeling overwhelmed and anxious, can you help me calm down?',
  },
  {
    id: 'healthy-food',
    name: 'Healthy Food',
    category: 'Home & Support',
    icon: 'Apple',
    description: 'Budget-conscious meal planning, shopping lists and recipes using affordable, culturally relevant ingredients — with support for specific diets and weight management.',
    examplePrompt: 'Plan a healthy week of meals for a family of four on a tight budget',
  },
  {
    id: 'money-coach',
    name: 'Money Coach',
    category: 'Money & Work',
    icon: 'PiggyBank',
    description: 'Preventative financial literacy and money management support — covering budgeting, savings planning, emergency funds, investment basics, insurance literacy and tax guidance.',
    examplePrompt: 'Help me build a monthly budget and start saving',
  },
  {
    id: 'earn-extra-cash',
    name: 'Earn Extra Cash',
    category: 'Money & Work',
    icon: 'Banknote',
    description: 'Side-hustle and micro-income guidance — helping you identify, validate and launch realistic income opportunities with social media selling, WhatsApp Business setup and scam awareness.',
    examplePrompt: 'What are some realistic ways I can earn extra money on weekends?',
  },
  {
    id: 'my-garden',
    name: 'My Garden',
    category: 'Home & Support',
    icon: 'Flower2',
    description: 'Home, food and urban container gardening support — covering planting calendars, soil preparation, pest identification, water-saving strategies and seasonal maintenance.',
    examplePrompt: 'What vegetables can I grow in containers on my balcony right now?',
  },
  {
    id: 'my-job-career',
    name: 'My Job & Career',
    category: 'Money & Work',
    icon: 'Briefcase',
    description: 'End-to-end employability and career development support — covering CV writing, cover letters, interview preparation, skills gap analysis, salary negotiation and job search strategies.',
    examplePrompt: 'Help me update my CV and prepare for a job interview',
  },
  {
    id: 'business-assist',
    name: 'Business Assist',
    category: 'Money & Work',
    icon: 'BarChart2',
    description: 'Comprehensive entrepreneurship and business management support — covering startup validation, business plans, pricing, marketing, cashflow, compliance and growth frameworks.',
    examplePrompt: 'I want to start a small business, where do I begin?',
  },
  {
    id: 'child-care',
    name: 'Child Care',
    category: 'Home & Support',
    icon: 'Baby',
    description: 'Comprehensive parenting support from early childhood through the teenage years — covering behaviour management, discipline, digital parenting, bullying and emotional intelligence.',
    examplePrompt: 'My teenager is becoming withdrawn and difficult, how do I approach this?',
  },
  {
    id: 'elderly-care',
    name: 'Elderly Care',
    category: 'Home & Support',
    icon: 'UserCheck',
    description: 'Senior support and caregiver guidance — covering daily care routines, chronic conditions, medication management, fall prevention, dementia support and caregiver burnout relief.',
    examplePrompt: 'My elderly mother is becoming forgetful, how do I support her at home?',
  },
];

export const INDUSTRIES_LIST: Industry[] = [
  {
    id: 'insurance',
    name: 'Insurance',
    icon: 'Shield',
    description: 'Increase policyholder engagement and retention through everyday family support',
  },
  {
    id: 'banking',
    name: 'Banking',
    icon: 'Building2',
    description: 'Deepen customer relationships with practical financial and household guidance',
  },
  {
    id: 'telco',
    name: 'Telco',
    icon: 'Smartphone',
    description: 'Add value beyond connectivity with family-focused support services',
  },
  {
    id: 'retail-rewards',
    name: 'Retail Rewards',
    icon: 'Gift',
    description: 'Transform loyalty programs into daily household value',
  },
  {
    id: 'employers-eap',
    name: 'Employers/EAP',
    icon: 'Users',
    description: 'Support employee wellbeing across work, money, health, and family life',
  },
  {
    id: 'government-ngo',
    name: 'Government/NGO',
    icon: 'Globe',
    description: 'Scale social impact with accessible support for communities',
  },
];

export const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'free',
    name: 'Free',
    monthlyPrice: 0,
    annualPrice: 0,
    members: 1,
    assistants: 5,
    messages: 50,
    features: [
      '1 family member',
      '5 assistants',
      '50 messages per month',
      'WhatsApp delivery',
      'Local language support',
    ],
  },
  {
    id: 'standard',
    name: 'Standard',
    monthlyPrice: 149,
    annualPrice: 1430,
    members: 3,
    assistants: 15,
    messages: 300,
    features: [
      '3 family members',
      '15 assistants',
      '300 messages per month',
      'WhatsApp delivery',
      'Local language support',
      'Priority support',
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    monthlyPrice: 299,
    annualPrice: 2870,
    members: 5,
    assistants: '22+',
    messages: 'Unlimited',
    features: [
      '5 family members',
      '22+ assistants',
      'Unlimited messages',
      'WhatsApp delivery',
      'Local language support',
      'Priority support',
      'Advanced features',
    ],
    highlighted: true,
  },
];

export const NAV_ITEMS: NavItem[] = [
  { label: 'Platform', href: ROUTE_PATHS.PLATFORM },
  { label: 'Assistants', href: ROUTE_PATHS.ASSISTANTS },
  { label: 'For Business', href: ROUTE_PATHS.FOR_BUSINESS },
  { label: 'Schools', href: ROUTE_PATHS.SCHOOLS },
  { label: 'Family Access', href: ROUTE_PATHS.FAMILY_ACCESS },
  { label: 'Partners', href: ROUTE_PATHS.PARTNERS },
  { label: 'About', href: ROUTE_PATHS.ABOUT },
  { label: 'Contact', href: ROUTE_PATHS.CONTACT },
];

export const PROOF_BAR_ITEMS = [
  { icon: 'Users', text: '5 Family Members' },
  { icon: 'Sparkles', text: '22+ Assistants' },
  { icon: 'Tag', text: 'White Label Ready' },
  { icon: 'Gift', text: 'Rewards & Loyalty Ready' },
  { icon: 'Globe', text: 'Built for Africa' },
];

export const BUSINESS_SOLUTIONS = [
  {
    id: 'rewards-loyalty',
    icon: 'Gift',
    title: 'Rewards & Loyalty',
    description: 'Turn points into practical household support',
  },
  {
    id: 'eap-wellness',
    icon: 'Heart',
    title: 'EAP/Employee Wellness',
    description: 'Support employees across work and home life',
  },
  {
    id: 'vas',
    icon: 'Zap',
    title: 'VAS (Value Added Services)',
    description: 'Add meaningful value beyond core products',
  },
  {
    id: 'csi-esg',
    icon: 'Leaf',
    title: 'CSI/ESG Impact',
    description: 'Create measurable social impact at scale',
  },
  {
    id: 'bbbee-ed',
    icon: 'TrendingUp',
    title: 'B-BBEE/Enterprise Development',
    description: 'Support SME growth and economic inclusion',
  },
  {
    id: 'education-support',
    icon: 'GraduationCap',
    title: 'Education Support',
    description: 'Help families with learning and school success',
  },
  {
    id: 'financial-inclusion',
    icon: 'Wallet',
    title: 'Financial Inclusion',
    description: 'Practical money guidance for all income levels',
  },
  {
    id: 'sme-support',
    icon: 'Briefcase',
    title: 'SME Support',
    description: 'Help small businesses grow and succeed',
  },
  {
    id: 'agriculture-rural',
    icon: 'Sprout',
    title: 'Agriculture/Rural Support',
    description: 'Farming and rural livelihood guidance',
  },
  {
    id: 'multinational-localisation',
    icon: 'Globe',
    title: 'Multinational Localisation',
    description: 'Adapt global offerings for African markets',
  },
];

export const PARTNER_TYPES: Partner[] = [
  {
    id: 'country-partners',
    type: 'Country Partners',
    icon: 'Globe',
    title: 'Country Partners',
    description: 'Launch Sammo in your market with localisation and distribution rights',
  },
  {
    id: 'industry-solution',
    type: 'Industry Solution Partners',
    icon: 'Briefcase',
    title: 'Industry Solution Partners',
    description: 'Build sector-specific solutions on the Sammo platform',
  },
  {
    id: 'rewards-platforms',
    type: 'Rewards Platforms',
    icon: 'Star',
    title: 'Rewards Platforms',
    description: 'Integrate Sammo as a redemption option for loyalty programs',
  },
  {
    id: 'eap-providers',
    type: 'EAP Providers',
    icon: 'Heart',
    title: 'EAP Providers',
    description: 'Enhance employee assistance programs with everyday support',
  },
  {
    id: 'distribution-networks',
    type: 'Distribution Networks',
    icon: 'Network',
    title: 'Distribution Networks',
    description: 'Reach customers through your existing channels',
  },
];